import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmtINR } from "@/lib/format";
import { toast } from "sonner";
import { Check, X, Zap, Loader2, RefreshCw } from "lucide-react";

/**
 * BRS Review Queue — side-by-side "bank line ↔ voucher" cards for 35–65 % confidence
 * suggestions. Users confirm or reject each in one click.
 * Backend: GET /banking/brs/review, POST /banking/brs/confirm, POST /banking/brs/reject
 */
export default function BRSReview() {
  const [accounts, setAccounts] = useState([]);
  const [accountId, setAccountId] = useState("");
  const [lines, setLines] = useState([]);
  const [busy, setBusy] = useState(false);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    api.get("/banking/accounts").then(r => {
      setAccounts(r.data || []);
      if (r.data?.[0]) setAccountId(r.data[0].id);
    });
  }, []);

  const load = async (aid = accountId) => {
    if (!aid) return;
    setBusy(true);
    try {
      const r = await api.get("/banking/brs/review", { params: { bank_account_id: aid } });
      setLines(r.data.lines || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Load failed"); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [accountId]);

  const runAutoMatch = async () => {
    if (!accountId) return;
    setRunning(true);
    try {
      const r = await api.post("/banking/brs/auto-match", { bank_account_id: accountId });
      toast.success(`Auto-matched ${r.data.matched} · ${r.data.reviewed - r.data.matched} pending review`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Match failed"); }
    finally { setRunning(false); }
  };

  const confirm = async (lineId) => {
    try {
      await api.post("/banking/brs/confirm", { line_id: lineId });
      toast.success("Match confirmed & reconciled");
      setLines(ls => ls.filter(l => l.id !== lineId));
    } catch (e) { toast.error(e?.response?.data?.detail || "Confirm failed"); }
  };
  const reject = async (lineId) => {
    try {
      await api.post("/banking/brs/reject", { line_id: lineId });
      toast.success("Suggestion rejected");
      setLines(ls => ls.filter(l => l.id !== lineId));
    } catch (e) { toast.error(e?.response?.data?.detail || "Reject failed"); }
  };

  const badge = (c) => {
    if (c >= 60) return "text-emerald-600 bg-emerald-50 border-emerald-200";
    if (c >= 50) return "text-amber-600 bg-amber-50 border-amber-200";
    return "text-red-600 bg-red-50 border-red-200";
  };

  return (
    <div className="p-6 lg:p-8 space-y-5" data-testid="brs-review-page">
      <PageHeader
        eyebrow="Banking · BRS"
        title="BRS Review Queue"
        description="Confirm or reject mid-confidence auto-matches (35 – 65 %) between bank statement lines and vouchers. High-confidence matches (≥ 65 %) are reconciled automatically."
      />

      <div className="card-sharp p-4 flex items-end gap-3">
        <div className="min-w-[240px]">
          <div className="text-[10px] font-mono uppercase text-muted-foreground mb-1">Bank Account</div>
          <Select value={accountId} onValueChange={setAccountId}>
            <SelectTrigger className="rounded-sm" data-testid="brs-account-picker">
              <SelectValue placeholder="Pick account" />
            </SelectTrigger>
            <SelectContent>
              {accounts.map(a => (
                <SelectItem key={a.id} value={a.id} data-testid={`brs-account-${a.id}`}>
                  {a.name} · {a.bank_name} · {a.account_number.slice(-4)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="ml-auto flex gap-2">
          <Button variant="outline" onClick={() => load()} disabled={busy} className="rounded-sm" data-testid="brs-refresh">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><RefreshCw className="w-4 h-4 mr-1.5" /> Refresh</>}
          </Button>
          <Button onClick={runAutoMatch} disabled={running || !accountId}
                    className="rounded-sm bg-accent hover:bg-accent/90 text-white gap-1.5" data-testid="brs-run-automatch">
            {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Zap className="w-4 h-4" /> Run auto-match</>}
          </Button>
        </div>
      </div>

      {lines.length === 0 && !busy && (
        <div className="card-sharp p-10 text-center text-sm text-muted-foreground" data-testid="brs-empty">
          🎉 Nothing to review — no mid-confidence suggestions pending. Import a fresh statement or run
          auto-match to populate this queue.
        </div>
      )}

      <div className="space-y-3">
        {lines.map(l => {
          const amt = (l.debit || 0) + (l.credit || 0);
          const v = l.suggested_voucher || {};
          const c = l.suggested_confidence || 0;
          return (
            <div key={l.id} className="card-sharp p-4" data-testid={`brs-line-${l.id}`}>
              <div className="flex items-center justify-between mb-3 pb-2 border-b">
                <div className="flex items-center gap-3">
                  <span className={`text-[10px] font-mono px-2 py-0.5 border rounded-sm uppercase ${badge(c)}`}
                          data-testid={`brs-confidence-${l.id}`}>
                    {c}% match
                  </span>
                  <span className="text-xs text-muted-foreground font-mono">line {l.id.slice(0, 8)}</span>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => reject(l.id)}
                           className="rounded-sm gap-1.5 border-red-200 text-red-600 hover:bg-red-50"
                           data-testid={`brs-reject-${l.id}`}>
                    <X className="w-3.5 h-3.5" /> Reject
                  </Button>
                  <Button size="sm" onClick={() => confirm(l.id)}
                           className="rounded-sm gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white"
                           data-testid={`brs-confirm-${l.id}`}>
                    <Check className="w-3.5 h-3.5" /> Confirm match
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="border-r pr-4">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1">
                    Bank statement line
                  </div>
                  <div className="text-sm mb-2">
                    <span className="font-mono text-xs">{l.date}</span>
                    <span className="ml-2 text-muted-foreground">·</span>
                    <span className="ml-2 tabular-nums font-medium">{fmtINR(amt)}</span>
                    <span className="ml-2 text-[10px] font-mono uppercase text-muted-foreground">
                      {l.debit > 0 ? "DR" : "CR"}
                    </span>
                  </div>
                  <div className="text-xs">{l.description || "—"}</div>
                </div>
                <div className="pl-2">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1">
                    Suggested voucher
                  </div>
                  <div className="text-sm mb-2">
                    <span className="font-mono text-xs">{v.date}</span>
                    <span className="ml-2 text-muted-foreground">·</span>
                    <span className="ml-2 tabular-nums font-medium">{fmtINR(v.amount || 0)}</span>
                    <span className="ml-2 text-[10px] font-mono uppercase text-accent">{v.type}</span>
                  </div>
                  <div className="text-xs" title={v.narration}>{v.narration || "—"}</div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
